import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Briefcase, Check, CheckCheck, Clock, Copy, CornerUpLeft, Flag, Forward, MoreVertical, Pencil, Pin, Store, Trash2, User } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ftime } from "@/lib/format";
import { RichText } from "@/lib/richtext";
import { LocationCard, MediaFile, VoicePlayer } from "@/components/chat/Media";
import { LANG_LABEL, TranslationBox, translate } from "@/components/member/NetworkAI";
import { Languages } from "lucide-react";

export const QUICK = ["👍", "❤️", "😂", "🙏", "👏", "🔥"];

function Card({ icon: Icon, title, sub, to, testId }) {
  const body = (
    <div className="flex items-center gap-2 p-2 border border-mr-border rounded-[3px] bg-mr-bg min-w-[200px]" data-testid={testId}>
      <Icon size={20} className="text-mr-accent shrink-0" />
      <div className="min-w-0"><p className="text-sm font-medium truncate">{title}</p>{sub && <p className="text-xs text-mr-muted truncate">{sub}</p>}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function Transcript({ m }) {
  const [text, setText] = useState(m.transcription);
  const [busy, setBusy] = useState(false);
  if (text) return <p className="text-xs text-mr-text2 mt-1 italic" data-testid="msg-transcription">{text}</p>;
  return <button data-testid="msg-transcribe-button" disabled={busy} className="text-[11px] text-mr-accent mt-1" onClick={() => {
    setBusy(true);
    api.post(`/ai/transcribe/${m.id}`).then(({ data }) => setText(data.transcription || "(silence)")).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  }}>{busy ? "Transcription…" : "Transcrire"}</button>;
}

function Body({ m }) {
  const meta = m.meta || {};
  switch (m.kind) {
    case "vocal": return <div><VoicePlayer id={m.media_file_ids[0]} meta={meta} /><Transcript m={m} /></div>;
    case "image":
    case "video": return <div className="space-y-1">{m.media_file_ids.map((f) => <MediaFile key={f} id={f} kind={m.kind} />)}{m.content && <RichText text={m.content} />}</div>;
    case "document": return <div className="space-y-1"><MediaFile id={m.media_file_ids[0]} kind="document" meta={meta} />{m.content && <RichText text={m.content} />}</div>;
    case "localisation": return <LocationCard meta={meta} />;
    case "contact": return <Card icon={User} title={meta.name} sub={meta.company} to={`/app/reseau/${meta.user_id}`} testId="msg-contact-card" />;
    case "operation": return <Card icon={Briefcase} title={meta.title} sub="Opération Monrezo" to={`/app/operations/${meta.operation_id}`} testId="msg-operation-card" />;
    case "fournisseur": return <Card icon={Store} title={meta.name} sub={[meta.phone, meta.note].filter(Boolean).join(" · ")} testId="msg-supplier-card" />;
    case "sticker": return <img src={meta.url} alt="Sticker" className="w-28 h-28" data-testid="msg-sticker" />;
    default: return <RichText text={m.content} />;
  }
}

function Receipt({ r }) {
  if (r === "en_attente") return <Clock size={12} aria-label="En attente d'envoi" data-testid="receipt-pending" />;
  if (r === "lu") return <CheckCheck size={13} className="text-mr-accent" aria-label="Lu" data-testid="receipt-read" />;
  if (r === "distribue") return <CheckCheck size={13} aria-label="Distribué" data-testid="receipt-delivered" />;
  return <Check size={13} aria-label="Envoyé" data-testid="receipt-sent" />;
}

export function Bubble({ m, mine, showName, canPin, actions, highlight }) {
  const deleted = m.status === "supprime";
  const sticker = m.kind === "sticker";
  const [tr, setTr] = useState(null);
  const doTranslate = (target) => translate("message", m.id, target).then(setTr).catch((e) => toast.error(errMsg(e)));
  return (
    <div id={`msg-${m.id}`} className={`group flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`relative max-w-[82%] ${sticker ? "" : `px-3 py-2 rounded-[3px] text-[15px] border ${mine ? "bg-mr-raised border-mr-border border-l-2 border-l-mr-accent" : "bg-mr-surface border-mr-border"}`} ${highlight ? "ring-2 ring-mr-accent" : ""}`}
        data-testid="thread-message">
        {showName && !mine && <p className="text-xs font-semibold text-mr-accent mb-0.5">{m.sender_name}</p>}
        {m.forwarded && <p className="text-[11px] italic text-mr-muted flex items-center gap-1 mb-0.5"><Forward size={11} /> Transféré</p>}
        {m.reply && (
          <button onClick={() => actions.jump(m.reply.id)} className="block w-full text-left text-xs border-l-2 border-mr-accent bg-mr-bg px-2 py-1 mb-1 rounded-[2px]" data-testid="msg-reply-quote">
            <span className="font-semibold">{m.reply.sender_name}</span><br /><span className="text-mr-text2 line-clamp-2">{m.reply.text}</span>
          </button>
        )}
        {deleted ? <p className="italic text-mr-muted text-sm" data-testid="msg-deleted">Message supprimé</p> : <Body m={m} />}
        {tr && !deleted && <TranslationBox t={tr} onClose={() => setTr(null)} />}
        <p className="text-[10px] text-mr-muted mt-1 flex items-center justify-end gap-1 tabular">
          {m.edited_at && !deleted && <span>modifié ·</span>}
          {ftime(m.created_at)}
          {mine && <Receipt r={m.receipt} />}
        </p>
        {Object.keys(m.reactions || {}).length > 0 && (
          <div className="absolute -bottom-3 right-2 flex gap-0.5 bg-mr-bg border border-mr-border rounded-full px-1.5 text-xs" data-testid="msg-reactions">
            {Object.entries(m.reactions).map(([e, n]) => <button key={e} onClick={() => actions.react(m, e)} className={m.my_reactions?.includes(e) ? "font-bold" : ""}>{e}{n > 1 ? n : ""}</button>)}
          </div>
        )}
        {!deleted && m.receipt !== "en_attente" && (
          <div className={`absolute top-1 ${mine ? "-left-9" : "-right-9"} opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity`}>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="msg-menu-button" aria-label="Actions" className="w-8 h-8 grid place-items-center text-mr-muted hover:text-mr-text"><MoreVertical size={16} /></DropdownMenuTrigger>
              <DropdownMenuContent align={mine ? "end" : "start"} className="w-52">
                <div className="flex justify-between px-1 py-1">
                  {QUICK.map((e) => <DropdownMenuItem key={e} data-testid={`msg-react-${e}`} onClick={() => actions.react(m, e)} className="text-lg px-1.5 cursor-pointer hover:scale-125 transition-transform">{e}</DropdownMenuItem>)}
                </div>
                <DropdownMenuItem data-testid="msg-action-reply" onClick={() => actions.reply(m)}><CornerUpLeft size={14} className="mr-2" /> Répondre</DropdownMenuItem>
                <DropdownMenuItem data-testid="msg-action-forward" onClick={() => actions.forward(m)}><Forward size={14} className="mr-2" /> Transférer</DropdownMenuItem>
                {(m.content || m.transcription) && Object.entries(LANG_LABEL).map(([k, l]) => <DropdownMenuItem key={k} data-testid={`msg-action-translate-${k}`} onClick={() => doTranslate(k)}><Languages size={14} className="mr-2" /> Traduire : {l}</DropdownMenuItem>)}
                {m.content && <DropdownMenuItem data-testid="msg-action-copy" onClick={() => actions.copy(m)}><Copy size={14} className="mr-2" /> Copier</DropdownMenuItem>}
                {canPin && <DropdownMenuItem data-testid="msg-action-pin" onClick={() => actions.pin(m)}><Pin size={14} className="mr-2" /> Épingler / désépingler</DropdownMenuItem>}
                {mine && m.kind === "texte" && <DropdownMenuItem data-testid="msg-action-edit" onClick={() => actions.edit(m)}><Pencil size={14} className="mr-2" /> Modifier</DropdownMenuItem>}
                <DropdownMenuItem data-testid="msg-action-delete-me" onClick={() => actions.remove(m, "me")}><Trash2 size={14} className="mr-2" /> Supprimer pour moi</DropdownMenuItem>
                {mine && <DropdownMenuItem data-testid="msg-action-delete-all" onClick={() => actions.remove(m, "all")}><Trash2 size={14} className="mr-2" /> Supprimer pour tous</DropdownMenuItem>}
                {!mine && <DropdownMenuItem data-testid="msg-action-report" onClick={() => actions.report(m)}><Flag size={14} className="mr-2" /> Signaler</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>
  );
}
